/**
 * What a good winder catalog image looks like, and how to say so.
 *
 * ADVISORY ONLY — nothing here rejects a file. That is the same call
 * `BrandBannerFallbacks` made and for the same reason: a curator cannot fix a
 * rejected image from inside this tool, so refusing it just loses the winder
 * its picture. An off-spec image still uploads, still renders, and is still
 * better than the generic glyph the app falls back to.
 *
 * WHY 1:1. The RN winder detail screen draws its hero into a square box on a
 * landscape tablet (mirroring ReferenceDetailScreen) and a 500x350 box
 * otherwise, both with `resizeMode: contain`. A square source fills the square
 * slot exactly; anything else is matted onto a themed background rather than
 * cropped. Square is also what the real catalogue already looks like — the
 * retailer product feeds these come from ship square shots.
 *
 * WHY 900px. The hero occupies up to 500pt and an iPad renders at 2x-3x, so on
 * paper ~1200px would be the comfortable floor. Measured against the real
 * catalogue that threshold is useless: 29 of the 30 stored images are exactly
 * 1:1 and almost all are 900x900, because that is what the Wolf product feed
 * ships — a warning that fires on 29 of 30 images is one nobody reads. 900
 * fires on exactly the one genuinely undersized image (679px) and stays quiet
 * otherwise, which is what makes it worth showing at all.
 *
 * `uploadWinderImage` caps the long edge at 1600, which is still the number to
 * aim for; it never upscales, so a small source stays small.
 */

/** Recommended minimum width, in pixels, of a source image. */
export const WINDER_IMAGE_MIN_RECOMMENDED_WIDTH = 900;

/**
 * How far from square a source may be before it is worth mentioning.
 *
 * 0.85..1.18 is roughly "4:5 to 6:5" — inside that the mats are a few pixels
 * and nobody would notice. A 3:2 product shot (1.5) lands outside it and does
 * mat visibly in the square slot, which is worth telling the curator even
 * though it renders perfectly well.
 */
const SQUARE_MIN = 0.85;
const SQUARE_MAX = 1.18;

export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Why this image is less than ideal for the winder hero, or `null` if it is
 * fine.
 *
 * Ratio is reported before resolution: a non-square image is matted in every
 * slot on every device, whereas softness only shows on a large screen, so the
 * shape is the more useful thing to say first. Only one message is returned —
 * a stack of warnings on an image that will render fine reads as a rejection.
 */
export function winderImageWarning(
  dimensions: ImageDimensions,
): string | null {
  const { width, height } = dimensions;
  // 0 means "could not decode" — `resizeIfOversize` reports that rather than
  // throwing, and it is not evidence of anything about the image.
  if (width <= 0 || height <= 0) return null;

  const ratio = width / height;
  if (ratio < SQUARE_MIN || ratio > SQUARE_MAX) {
    return `${width}x${height} is not square — the app will letterbox it (1:1 recommended)`;
  }
  if (width < WINDER_IMAGE_MIN_RECOMMENDED_WIDTH) {
    return `Only ${width}px wide — may look soft (${WINDER_IMAGE_MIN_RECOMMENDED_WIDTH}px+ recommended)`;
  }
  return null;
}

/** "1600 x 1600" — what the curator picked, before any downscale. */
export function formatDimensions(dimensions: ImageDimensions): string {
  const { width, height } = dimensions;
  if (width <= 0 || height <= 0) return '';
  return `${width} × ${height}`;
}
